import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

/**
 * DebouncedFilterInput — small text filter with 400ms debounce.
 * Fires `onCommit(value)` after the user stops typing so we don't
 * fetch on every keystroke. `activeColor` is a Tailwind color name
 * (e.g. "emerald", "sky") used to visually indicate the filter is on.
 */
export function DebouncedFilterInput({
  value,
  placeholder,
  onCommit,
  testId,
  activeColor = 'violet',
  delay = 400,
  width = 'w-36',
}) {
  const [local, setLocal] = useState(value || '');
  const timerRef = useRef(null);
  // Keep local state in sync when parent resets (e.g. deep-linked filter change).
  useEffect(() => { setLocal(value || ''); }, [value]);

  const scheduleCommit = (v) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onCommit(v.trim());
    }, delay);
  };

  const activeClasses = {
    violet: 'bg-violet-50 border-violet-300 text-violet-700 placeholder-violet-400',
    emerald: 'bg-emerald-50 border-emerald-300 text-emerald-700 placeholder-emerald-400',
    sky: 'bg-sky-50 border-sky-300 text-sky-700 placeholder-sky-400',
    amber: 'bg-amber-50 border-amber-300 text-amber-700 placeholder-amber-400',
  }[activeColor] || 'bg-violet-50 border-violet-300 text-violet-700';

  const active = !!(local && local.trim());

  return (
    <div className={`relative inline-block ${width}`}>
      <input
        type="text"
        value={local}
        placeholder={placeholder}
        onChange={(e) => {
          const v = e.target.value;
          setLocal(v);
          scheduleCommit(v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            if (timerRef.current) clearTimeout(timerRef.current);
            onCommit((local || '').trim());
          }
        }}
        className={`w-full pl-2 pr-6 py-1.5 text-[11px] font-medium rounded-md border transition-colors ${
          active ? activeClasses : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
        }`}
        data-testid={testId}
      />
      {active && (
        <button
          type="button"
          onClick={() => {
            setLocal('');
            if (timerRef.current) clearTimeout(timerRef.current);
            onCommit('');
          }}
          className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          title="Clear filter"
          data-testid={`${testId}-clear`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

export default DebouncedFilterInput;
